import React, { useState, useEffect, createRef } from 'react';
import ReactAvatarEditor from 'react-avatar-editor';
import { isWeb } from '../../utils/util';
import intl from 'react-intl-universal';
import { fixIosFocusProblem } from '../../utils/dom';
import './AvatarEditor.scss';

export interface IAvatarEditor {
  image: File | string;
  onSubmit: (obj?: any) => PromiseLike<any>;
  onCancel: () => void;
  useName?: boolean;
  disabled?: boolean;
}

let _SINGLE_ZOOM_ = 1;

const AvatarEditor = (props: IAvatarEditor) => {
  const WIDTH = Math.min(window.innerWidth, 750);
  const HEIGHT = window.innerHeight;

  const FRAME = Math.min(WIDTH, HEIGHT - 50) * 0.84;
  const BORDER_H = (WIDTH - FRAME) / 2;
  const BORDER_V = (HEIGHT - FRAME) / 2;

  const [zoom, setZoom] = useState(1);

  const syncZoom = (z: number) => {
    setZoom(z);
    _SINGLE_ZOOM_ = z;
  };

  const wrapperRef = createRef<HTMLDivElement>();
  const reactAvatarRef = createRef<ReactAvatarEditor>();

  const webUser = isWeb();

  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    // 移动端
    let base = 0;
    let currentZoom = 1;
    _SINGLE_ZOOM_ = 1;
    let wrapperDom = wrapperRef.current!;

    const onTouchStart = (e: TouchEvent) => {
      let { touches } = e;
      if (touches && touches.length == 2) {
        // 双指状态
        let line1 = Math.abs(touches[0].pageX - touches[1].pageX);
        let line2 = Math.abs(touches[0].pageY - touches[1].pageY);

        base = Math.max(line1, line2);
        currentZoom = _SINGLE_ZOOM_;

        wrapperDom.addEventListener('touchmove', onTouchMove);
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      try {
        wrapperDom.removeEventListener('touchmove', onTouchMove);
      } catch (e) {
        console.log(`touch end error`, e);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      try {
        let { touches } = e;
        if (touches && touches.length == 2) {
          // 双指状态
          let line1 = Math.abs(touches[0].pageX - touches[1].pageX);
          let line2 = Math.abs(touches[0].pageY - touches[1].pageY);

          let between = Math.abs(Math.max(line1, line2));
          let percent = between / base;
          currentZoom +=
            percent > 1 ? (percent - 1) / 5 : (-1 * (1 - percent)) / 5;

          currentZoom = Math.max(1, Math.min(4, currentZoom));
          syncZoom(currentZoom);
        }
      } catch (e) {
        console.log(`error`, e);
      }
    };

    if (!webUser && !props.disabled) {
      wrapperDom.addEventListener('touchstart', onTouchStart);
      wrapperDom.addEventListener('touchend', onTouchEnd);
    }

    return () => {
      if (wrapperDom) {
        wrapperDom.removeEventListener('touchstart', onTouchStart);
        wrapperDom.removeEventListener('touchend', onTouchEnd);
      }
    };
  }, [webUser]);

  useEffect(() => {
    const handleZoomEvent = (e: any) => {
      console.log(`handle zoom event`, e);
      syncZoom(Number(e.data));
    };

    window.addEventListener('avatar-editor-zoom', handleZoomEvent);

    return () => {
      window.removeEventListener('avatar-editor-zoom', handleZoomEvent);
    };
  }, [zoom]);

  const [showButton, setShowButton] = useState(true);
  const [animate, setAnimate] = useState(false);
  const [name, setName] = useState('');

  const onSubmit = () => {
    setShowButton(false);
    setAnimate(true);
    setShowMessage(true);

    let base64edAvatar = '';

    if (
      props.disabled &&
      props.image &&
      String(props.image).indexOf('http') == 0
    ) {
      // base64edAvatar = props.default;
    } else {
      if (reactAvatarRef.current) {
        //如果嫌这个太不清晰了，请用getImage获取的原始的大小
        const canvas = reactAvatarRef.current.getImage(); //  getImage getImageScaledToCanvas

        if(canvas.width > 750) {
          let newCanvas = window.document.createElement('canvas');
          let context = newCanvas.getContext('2d');
          newCanvas.width = 750;
          newCanvas.height = 750;
          context!.drawImage(canvas, 0, 0, 750, 750);

          base64edAvatar = newCanvas.toDataURL('image/jpeg', 1);
        }else {
          base64edAvatar = canvas.toDataURL('image/jpeg', 1);
        }
        const head = 'data:image/jpeg;base64,';
        base64edAvatar = base64edAvatar.slice(head.length);
      }
    }

    props
      .onSubmit({
        image: base64edAvatar,
        name
      })
      .then(res => {
        if (res === false) {
          setAnimate(false);
          setShowButton(true);
          setShowMessage(false);
        } else {
          setAnimate(false);
          props.onCancel();
        }
      });
  };

  return (
    <div className="avatar-editor" ref={wrapperRef}>
      <ReactAvatarEditor
        ref={reactAvatarRef}
        image={props.image}
        width={FRAME}
        height={FRAME}
        border={[BORDER_H, BORDER_V]}
        scale={zoom}
      />

      <div className="border" style={{ width: FRAME, height: FRAME }}>
        <img src={require('../../assets/avatar-editor-border.png')} />
        <img
          src={require('../../assets/avatar-editor-border-tip.png')}
          className="tip"
        />

        {webUser && !props.disabled ? (
          <div className="operation">
            <a
              onClick={() => {
                let newZoom = zoom + 0.1;
                syncZoom(Math.max(1, Math.min(4, newZoom)));
              }}
            >
              {intl.get('ZoomOut')}
            </a>
            <a
              onClick={() => {
                let newZoom = zoom - 0.1;
                syncZoom(Math.max(1, Math.min(4, newZoom)));
              }}
            >
              {intl.get('ZoomIn')}
            </a>
          </div>
        ) : null}

        {animate ? (
          <div
            className="scan-wrapper animate"
            style={{ width: FRAME, height: FRAME }}
          >
            <img
              className="uptodown"
              src={require('../../assets/avatar-editor-scan.png')}
            />
            <img
              className="downtoup"
              src={require('../../assets/avatar-editor-scan.png')}
            />
          </div>
        ) : null}

        {showMessage ? (
          <div className="face-loading">{intl.get('FaceLoading')}</div>
        ) : null}

        {!props.useName && (
          <div className="input-row">
            <input
              type="text"
              className={'name-input'}
              style={{ color: '#fff' }}
              placeholder={intl.get('Name placeholder')}
              onChange={e => setName(e.target.value)}
              value={name}
              // 这个的细节描述请看ReadMe
              onBlur={e => fixIosFocusProblem(e.target)}
            />
          </div>
        )}
      </div>

      {showButton ? (
        <div className="last-btn-row bt">
          <div className={'btn'} onClick={props.onCancel}>
            {intl.get('cancel')}
          </div>
          <div className={'btn'} onClick={onSubmit}>
            {intl.get('ok')}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AvatarEditor;
